<template>
  <div class="app-shell" :class="{ 'is-collapsed': collapsed }" @keydown="handleDrawerKeydown">
    <aside
      ref="sidebarRef"
      class="app-shell__sidebar"
      :class="{ 'is-collapsed': collapsed }"
      :inert="mobileDrawerClosed ? true : undefined"
      :aria-hidden="mobileDrawerClosed ? 'true' : undefined"
    >
      <div class="app-shell__brand">
        <span class="app-shell__brand-mark" aria-hidden="true">VTC LD</span>
        <span class="app-shell__brand-name">AI Writing System</span>
      </div>

      <nav aria-label="Primary" class="app-nav" @click="closeMobileDrawer">
        <button
          v-for="item in navItems"
          :key="item.view"
          type="button"
          :class="['app-nav__item', { 'is-active': item.view === activeView }]"
          :aria-label="item.label"
          :aria-current="item.view === activeView ? 'page' : undefined"
          @click="$emit('navigate', item.view)"
        >
          <svg
            v-if="item.paths?.length"
            class="app-nav__icon"
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            focusable="false"
          >
            <path v-for="path in item.paths" :key="path" :d="path"></path>
          </svg>
          <span v-else class="app-nav__fallback" aria-hidden="true">{{ navFallback(item) }}</span>
          <span class="app-nav__label">{{ item.label }}</span>
        </button>
      </nav>

      <div class="app-shell__account">
        <div class="app-shell__account-details">
          <p class="app-shell__role">{{ roleLabel || account.role }}</p>
          <p class="app-shell__name">{{ account.display_name || account.username || 'Account' }}</p>
        </div>
        <button type="button" class="ui-button ui-button--quiet app-shell__logout" aria-label="Sign out" @click="$emit('logout')">
          <svg class="app-shell__logout-icon" aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" focusable="false">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <path d="M16 17l5-5-5-5"></path>
            <path d="M21 12H9"></path>
          </svg>
          <span class="app-shell__logout-label">Sign out</span>
        </button>
      </div>
    </aside>

    <button
      ref="navToggleRef"
      type="button"
      class="app-shell__nav-toggle"
      :aria-label="collapsed ? 'Open navigation' : 'Close navigation'"
      :aria-expanded="String(!collapsed)"
      @click="emit('toggle-nav')"
    >
      <span aria-hidden="true">☰</span>
      <span class="sr-only">Toggle navigation</span>
    </button>

    <main
      class="app-shell__main"
      :inert="mobileDrawerOpen ? true : undefined"
      :aria-hidden="mobileDrawerOpen ? 'true' : undefined"
    >
      <slot />
    </main>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  roleLabel: {
    type: String,
    default: '',
  },
  navItems: {
    type: Array,
    default: () => [],
  },
  activeView: {
    type: String,
    default: '',
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  account: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['navigate', 'logout', 'toggle-nav'])
const isMobile = ref(false)
const sidebarRef = ref(null)
const navToggleRef = ref(null)
const mobileDrawerClosed = computed(() => isMobile.value && props.collapsed)
const mobileDrawerOpen = computed(() => isMobile.value && !props.collapsed)
const firstDrawerAction = computed(() => sidebarRef.value?.querySelector('.app-nav__item'))
const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const syncViewport = () => {
  isMobile.value = window.matchMedia('(max-width: 767px)').matches
}

const closeMobileDrawer = () => {
  if (isMobile.value && !props.collapsed) {
    emit('toggle-nav')
  }
}

const handleEscape = event => {
  if (event.key === 'Escape' && isMobile.value && !props.collapsed) {
    emit('toggle-nav')
  }
}

const handleDrawerKeydown = event => {
  if (event.key !== 'Tab' || !mobileDrawerOpen.value) return

  const drawerActions = Array.from(sidebarRef.value?.querySelectorAll(focusableSelector) || [])
  const focusableElements = [...drawerActions, navToggleRef.value].filter(Boolean)
  if (!focusableElements.length) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  const focusIsContained = sidebarRef.value?.contains(document.activeElement) || document.activeElement === navToggleRef.value
  if (!focusIsContained) {
    event.preventDefault()
    ;(event.shiftKey ? lastElement : firstElement).focus()
  } else if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

watch(mobileDrawerClosed, async (closed, wasClosed) => {
  if (closed && !wasClosed) {
    await nextTick()
    navToggleRef.value?.focus()
  }
})

watch(mobileDrawerOpen, async (open, wasOpen) => {
  if (open && !wasOpen) {
    await nextTick()
    firstDrawerAction.value?.focus()
  }
})

const navFallback = item => {
  const words = String(item?.label || item?.view || '').trim().split(/[\s/]+/).filter(Boolean)
  return words.slice(0, 2).map(word => word[0]).join('').toUpperCase() || '•'
}

onMounted(() => {
  syncViewport()
  window.addEventListener('resize', syncViewport)
  window.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncViewport)
  window.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.app-shell {
  display: grid;
  grid-template-columns: var(--shell-sidebar) minmax(0, 1fr);
  min-height: 100vh;
  background: var(--canvas);
}

.app-shell.is-collapsed {
  grid-template-columns: var(--shell-rail) minmax(0, 1fr);
}

.app-shell__sidebar {
  display: flex;
  position: relative;
  z-index: 2;
  flex-direction: column;
  min-height: 100vh;
  border-right: 1px solid var(--border);
  background: var(--surface);
}

.app-shell__brand {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  min-height: 4.5rem;
  border-bottom: 1px solid var(--border);
  padding: var(--space-4);
}

.app-shell__brand-mark {
  color: var(--accent-strong);
  font-family: var(--font-academic);
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: -0.04em;
}

.app-shell__brand-name {
  color: var(--ink);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.app-nav {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-2);
}

.app-nav__item {
  display: flex;
  width: 100%;
  min-height: 2.5rem;
  align-items: center;
  gap: var(--space-3);
  border: 0;
  border-left: 2px solid transparent;
  border-radius: var(--radius-control);
  padding: var(--space-2) var(--space-3);
  background: transparent;
  color: var(--ink-muted);
  font-size: 0.875rem;
  font-weight: 700;
  text-align: left;
}

.app-nav__icon,
.app-shell__logout-icon {
  width: 1.5rem;
  height: 1.5rem;
  flex: 0 0 1.5rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.app-nav__fallback {
  display: inline-grid;
  width: 1.5rem;
  height: 1.5rem;
  flex: 0 0 1.5rem;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.app-nav__item:hover,
.app-nav__item:focus-visible {
  background: var(--surface-muted);
  color: var(--ink);
}

.app-nav__item.is-active {
  border-left: 2px solid var(--accent);
  background: var(--accent-soft);
  color: var(--accent-strong);
}

.app-shell__account {
  margin-top: auto;
  border-top: 1px solid var(--border);
  padding: var(--space-4);
}

.app-shell__role,
.app-shell__name {
  margin: 0;
}

.app-shell__role {
  color: var(--ink-muted);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.app-shell__name {
  margin-top: var(--space-1);
  overflow: hidden;
  font-size: 0.875rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell__logout {
  margin-top: var(--space-3);
  padding-left: 0;
}

.app-shell__nav-toggle {
  position: fixed;
  z-index: 3;
  top: var(--space-3);
  left: calc(var(--shell-sidebar) - 1.25rem);
  display: inline-grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: var(--surface);
  color: var(--ink);
}

.is-collapsed .app-shell__nav-toggle {
  left: calc(var(--shell-rail) - 1.25rem);
}

.is-collapsed .app-shell__brand,
.is-collapsed .app-shell__account {
  padding-right: var(--space-2);
  padding-left: var(--space-2);
}

.is-collapsed .app-shell__brand {
  justify-content: center;
}

.is-collapsed .app-shell__brand-name,
.is-collapsed .app-nav__label,
.is-collapsed .app-shell__account-details,
.is-collapsed .app-shell__logout-label {
  display: none;
}

.is-collapsed .app-shell__logout {
  width: 2.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  margin: 0 auto;
  padding: 0;
}

.is-collapsed .app-nav__item {
  justify-content: center;
  padding-right: var(--space-2);
  padding-left: var(--space-2);
}

.app-shell__main {
  min-width: 0;
  padding: var(--space-8);
}

@media (min-width: 768px) and (max-width: 1023px) {
  .app-shell,
  .app-shell.is-collapsed {
    grid-template-columns: var(--shell-rail) minmax(0, 1fr);
  }

  .app-shell__brand,
  .app-shell__account {
    padding-right: var(--space-2);
    padding-left: var(--space-2);
  }

  .app-shell__brand {
    justify-content: center;
  }

  .app-shell__brand-name,
  .app-nav__label,
  .app-shell__account-details,
  .app-shell__logout-label,
  .app-shell__nav-toggle {
    display: none;
  }

  .app-shell__logout {
    width: 2.5rem;
    min-width: 2.5rem;
    min-height: 2.5rem;
    margin: 0 auto;
    padding: 0;
  }

  .app-nav__item {
    justify-content: center;
    padding-right: var(--space-2);
    padding-left: var(--space-2);
  }

  .app-shell__main {
    padding: var(--space-6);
  }
}

@media (max-width: 767px) {
  .app-shell,
  .app-shell.is-collapsed {
    display: block;
  }

  .app-shell__sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    width: min(18rem, 86vw);
    transform: translateX(0);
    transition: transform 180ms ease;
  }

  .app-nav__item {
    min-height: 44px;
  }

  .app-shell__sidebar.is-collapsed {
    transform: translateX(-100%);
  }

  .app-shell__sidebar.is-collapsed .app-shell__brand-name,
  .app-shell__sidebar.is-collapsed .app-nav__label,
  .app-shell__sidebar.is-collapsed .app-shell__account-details,
  .app-shell__sidebar.is-collapsed .app-shell__logout-label {
    display: revert;
  }

  .app-shell__nav-toggle,
  .is-collapsed .app-shell__nav-toggle {
    top: var(--space-3);
    left: var(--space-3);
    width: 44px;
    height: 44px;
  }

  .app-shell:not(.is-collapsed) .app-shell__nav-toggle {
    left: min(calc(18rem - 1.25rem), calc(86vw - 1.25rem));
  }

  .app-shell__main {
    padding: 5rem var(--space-4) var(--space-6);
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-shell__sidebar {
    transition: none;
  }
}
</style>
